// [claude-code 2026-03-20] S3:T5 — notification system: DND blocklist, notificationType for Don't Show Again
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type ToastVariant =
  | "success"
  | "error"
  | "updating"
  | "info"
  | "reminder"
  | "vix"
  | "watch"
  | "risk-signal";

/** Toast placement: system -> bottom-left, RiskFlow -> bottom-right. */
export type ToastPosition = "bottom-left" | "bottom-right";

/** Unique notification type ID used for "Don't Show Again" blocklist */
export type NotificationType =
  | "vix-spike"
  | "system-update"
  | "connection-status"
  | "api-error"
  | "grok-auth"
  | "pre-market-reminder"
  | "news-alert"
  | "tilt-alert"
  | "trade-alert"
  | "iv-alert"
  | "riskflow-watch"
  | "risk-signal"
  | "agent-task"
  | "general";

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  description?: string;
  exiting?: boolean;
  /** If set, enables "Don't Show Again" button for this notification type */
  notificationType?: NotificationType;
  /** Placement: system = bottom-left, RiskFlow = bottom-right. */
  position?: ToastPosition;
  /** Optional CTA button */
  cta?: { label: string; onClick: () => void };
  /** Optional secondary CTA button */
  secondaryCta?: { label: string; onClick: () => void };
  /** Optional override for auto-dismiss duration */
  durationMs?: number;
  /** Optional release/status glyph rendered left of the toast message */
  leadingGlyph?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (
    message: string,
    variant?: ToastVariant,
    description?: string,
    notificationType?: NotificationType,
    position?: ToastPosition,
    cta?: { label: string; onClick: () => void },
    secondaryCta?: { label: string; onClick: () => void },
    durationMs?: number,
    leadingGlyph?: string,
  ) => string;
  dismissToast: (id: string) => void;
  /** Permanently block a notification type (Don't Show Again) */
  blockNotificationType: (type: NotificationType) => void;
  /** Check if a notification type is blocked */
  isNotificationBlocked: (type: NotificationType) => boolean;
  /** Reset all blocked notification types */
  resetBlockedNotifications: () => void;
  /** Get list of all blocked notification types */
  blockedTypes: NotificationType[];
  /** Show maintenance toast with "Update App" CTA (bottom-left, system) */
  showMaintenanceToast: (onUpdate: () => void) => string;
}

const DND_STORAGE_KEY = "fintheon:notification-blocklist";
const API_ERROR_CASCADE_DESCRIPTION =
  "Multiple backend errors are being grouped into this one notice.";

function loadBlocklist(): NotificationType[] {
  try {
    const raw = localStorage.getItem(DND_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveBlocklist(list: NotificationType[]) {
  localStorage.setItem(DND_STORAGE_KEY, JSON.stringify(list));
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  addToast: () => "",
  dismissToast: () => {},
  blockNotificationType: () => {},
  isNotificationBlocked: () => false,
  resetBlockedNotifications: () => {},
  blockedTypes: [],
  showMaintenanceToast: () => "",
});

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

function LegacyToastProvider({
  children,
  suppressToasts = false,
}: {
  children: ReactNode;
  suppressToasts?: boolean;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [blockedTypes, setBlockedTypes] =
    useState<NotificationType[]>(loadBlocklist);

  useEffect(() => {
    if (suppressToasts) setToasts([]);
  }, [suppressToasts]);

  const isNotificationBlocked = useCallback(
    (type: NotificationType) => blockedTypes.includes(type),
    [blockedTypes],
  );

  const blockNotificationType = useCallback((type: NotificationType) => {
    setBlockedTypes((prev) => {
      if (prev.includes(type)) return prev;
      const next = [...prev, type];
      saveBlocklist(next);
      return next;
    });
  }, []);

  const resetBlockedNotifications = useCallback(() => {
    setBlockedTypes([]);
    saveBlocklist([]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback(
    (
      message: string,
      variant: ToastVariant = "info",
      description?: string,
      notificationType?: NotificationType,
      position?: ToastPosition,
      cta?: { label: string; onClick: () => void },
      secondaryCta?: { label: string; onClick: () => void },
      durationMs?: number,
      leadingGlyph?: string,
    ): string => {
      if (suppressToasts) return "";

      // Skip if this notification type is blocked
      if (notificationType && blockedTypes.includes(notificationType)) {
        return "";
      }

      const toastPosition =
        notificationType === "riskflow-watch" ||
        notificationType === "risk-signal"
          ? "bottom-right"
          : "bottom-left";
      const existingApiErrorToast =
        notificationType === "api-error"
          ? toasts.find(
              (toast) =>
                toast.notificationType === "api-error" &&
                toast.variant === "error" &&
                !toast.exiting &&
                (toast.position ?? "bottom-left") === toastPosition,
            )
          : undefined;

      if (existingApiErrorToast) {
        setToasts((prev) =>
          prev.map((toast) =>
            toast.id === existingApiErrorToast.id
              ? {
                  ...toast,
                  message,
                  description:
                    toast.description &&
                    toast.description !== API_ERROR_CASCADE_DESCRIPTION
                      ? `${toast.description} ${API_ERROR_CASCADE_DESCRIPTION}`
                      : (description ?? API_ERROR_CASCADE_DESCRIPTION),
                  durationMs: Math.max(
                    toast.durationMs ?? 2500,
                    durationMs ?? 2500,
                  ),
                }
              : toast,
          ),
        );
        return existingApiErrorToast.id;
      }

      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const toast: Toast = {
        id,
        message,
        variant,
        description,
        notificationType,
        position: toastPosition,
        cta,
        secondaryCta,
        durationMs,
        leadingGlyph,
      };
      setToasts((prev) => [...prev, toast]);

      // Auto-dismiss (except 'updating' and toasts with CTAs which stay until manually dismissed)
      if (variant !== "updating" && !cta) {
        const delay =
          durationMs ??
          (variant === "error"
            ? 2500
            : variant === "reminder"
              ? 8000
              : variant === "watch" || variant === "risk-signal"
                ? 12000
                : variant === "vix"
                  ? 10000
                  : 4000);
        setTimeout(() => dismissToast(id), delay);
      }

      return id;
    },
    [dismissToast, blockedTypes, suppressToasts, toasts],
  );

  const showMaintenanceToast = useCallback(
    (onUpdate: () => void): string => {
      if (suppressToasts) return "";

      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const toast: Toast = {
        id,
        message: "Fintheon is updating...",
        variant: "updating",
        notificationType: "system-update",
        position: "bottom-left",
        cta: { label: "Update App", onClick: onUpdate },
      };
      setToasts((prev) => [...prev, toast]);
      return id;
    },
    [suppressToasts],
  );

  const visibleToasts = suppressToasts ? [] : toasts;

  return (
    <ToastContext.Provider
      value={{
        toasts: visibleToasts,
        addToast,
        dismissToast,
        blockNotificationType,
        isNotificationBlocked,
        resetBlockedNotifications,
        blockedTypes,
        showMaintenanceToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

const useLegacyToast = () => useContext(ToastContext);

export {
  UnifiedToastProvider as ToastProvider,
  useUnifiedToast as useToast,
} from "../components/notifications/UnifiedNotificationContext";
